import * as React from 'react';
import { Button, Tooltip } from '@wix/design-system';
import s from '../PlansGrid.scss';
import classNames from 'classnames';
import { RootStore, withInjectedRootStore } from 'providers';
import { useTranslation } from 'react-i18next';
import { usePageModeContext } from 'providers/PageMode/PageMode.provider';
import { useBreakpoint } from 'providers/BreakpointProvider';
import { MediaDevices, Theme } from 'enums';
import {
  InjectedExperimentsProps,
  withExperiments,
} from '@wix/wix-experiments-react';

interface SelectButtonProps extends InjectedExperimentsProps {
  onClick(): void;
  isSelected: boolean;
  isDisabled: boolean;
  isSticky?: boolean;
  rootStore: RootStore;
  isCurrentPlanProduct: boolean;
}

const SelectButtonComp: React.FC<SelectButtonProps> = (props) => {
  const { t } = useTranslation();
  const { isDisabled, isCurrentPlanProduct } = props;

  const getDisabledTooltipKey = () => {
    return isCurrentPlanProduct
      ? 'productEntry.current_plan.disabled_tooltip'
      : 'Advanced.Plans_second.upgrade_plan.disabled.tooltip';
  };

  return isDisabled ? (
    <div className={s.tooltipWrapper}>
      <Tooltip
        maxWidth={300}
        textAlign='start'
        placement='bottom'
        dataHook='product-select-disabled-tooltip'
        moveBy={{ x: 0, y: 5 }}
        content={t(getDisabledTooltipKey())}
      >
        <SelectButtonInternal {...props} />
      </Tooltip>
    </div>
  ) : (
    <SelectButtonInternal {...props} />
  );
};

const SelectButtonInternal: React.FC<SelectButtonProps> = ({
  onClick,
  isSelected,
  isSticky,
  isDisabled,
  rootStore,
  children,
  experiments,
}) => {
  const { theme } = usePageModeContext();
  const { currentBreakpoint } = useBreakpoint();
  const isDesktop = currentBreakpoint === MediaDevices.Desktop;
  const isMediumTableViewMode = rootStore.appStore.isMediumSizeTableView;
  const buttonSize = isMediumTableViewMode
    ? 'small'
    : isDesktop
      ? 'large'
      : isSticky
        ? 'medium'
        : 'small';
  const isDayful = theme === Theme.Dayful;
  const showSaleButton = rootStore.appStore.isInSale && !isDisabled;

  return (
    <div
      className={classNames(s.selectButtonWrapper, {
        [s.scaledButton]: isSticky,
      })}
    >
      <Button
        size={buttonSize}
        skin={isDayful ? 'standard' : 'premium'}
        dataHook='select-button'
        fullWidth={!isDayful}
        disabled={isDisabled}
        onClick={onClick}
        priority={isSelected && !isDisabled ? 'primary' : 'secondary'}
        className={classNames(
          s.selectButton,
          {
            'sale-button': showSaleButton,
            [s.selected]: isSelected && !isDisabled,
            [s.disabled]: isDisabled,
          },
          theme,
        )}
      >
        {children}
      </Button>
    </div>
  );
};

export const SelectButton = withExperiments(
  withInjectedRootStore(SelectButtonComp),
);
